"use client";
import React, { useState } from "react";
import { MobileHeader } from "../MobileHeader";
import { TabBar } from "../TabBar";
import Badge from "../Badge";

interface TipCategory {
  id: string;
  name: string;
  icon: string;
}

interface Tip {
  id: string;
  title: string;
  description: string;
  savings: string;
  categoryId: string;
  image?: string;
}

const WaterConservationTips: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [expandedTip, setExpandedTip] = useState<string | null>(null);
  
  const categories: TipCategory[] = [
    { 
      id: "all", 
      name: "All Tips", 
      icon: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/6af0b6ed9f50e71afeaf46286f3b389623f64a7e?placeholderIfAbsent=true" 
    },
    { 
      id: "bathroom", 
      name: "Bathroom", 
      icon: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/7f625e1c79b666a13d3e0d82b79da0d875317c0e?placeholderIfAbsent=true" 
    },
    { 
      id: "kitchen", 
      name: "Kitchen", 
      icon: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/5d9ba2832a13435439c685e0820b801486c32ddb?placeholderIfAbsent=true" 
    },
    { 
      id: "outdoor", 
      name: "Outdoor", 
      icon: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/cec797511849684c7dd7d9ef209d76fe9d7de662?placeholderIfAbsent=true" 
    },
    { 
      id: "laundry", 
      name: "Laundry", 
      icon: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/5d9ba2832a13435439c685e0820b801486c32ddb?placeholderIfAbsent=true" 
    }
  ];
  
  const tips: Tip[] = [
    {
      id: "1",
      title: "Shorter Showers",
      description: "Take shorter showers. Every minute you cut from your shower saves approximately 2.5 gallons of water. Try using a shower timer or playing songs that are 5 minutes long to time yourself.",
      savings: "15L per shower",
      categoryId: "bathroom",
      image: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/7f625e1c79b666a13d3e0d82b79da0d875317c0e?placeholderIfAbsent=true"
    },
    {
      id: "2",
      title: "Fix Leaky Faucets",
      description: "A dripping faucet can waste 20 gallons of water a day. Fixing it is often as simple as replacing a washer. Check all faucets in your home monthly for leaks.",
      savings: "20L per day",
      categoryId: "bathroom",
      image: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/5d9ba2832a13435439c685e0820b801486c32ddb?placeholderIfAbsent=true"
    },
    {
      id: "3",
      title: "Dishwasher Efficiency",
      description: "Only run your dishwasher when it's full. This can save up to 1,000 gallons of water per month. Modern dishwashers typically use less water than hand-washing dishes.",
      savings: "38L per load",
      categoryId: "kitchen",
      image: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/cc4213bd4e070418d430078b81f4f68ed32349b7?placeholderIfAbsent=true"
    },
    {
      id: "4",
      title: "Water-Efficient Landscaping",
      description: "Choose plants that are native to your region or are drought-resistant for your landscaping. Group plants with similar water needs together to avoid overwatering some while underwatering others.",
      savings: "50-70% outdoor usage",
      categoryId: "outdoor",
      image: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/343d1e62d53da6f82818e98ac5b7c83c3cd33ae4?placeholderIfAbsent=true"
    },
    {
      id: "5",
      title: "Turn Off While Brushing",
      description: "Turn off the water while brushing your teeth. This simple habit can save up to 8 gallons of water per day. The same applies when washing your hands or shaving.",
      savings: "8L per day",
      categoryId: "bathroom",
      image: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/7f625e1c79b666a13d3e0d82b79da0d875317c0e?placeholderIfAbsent=true"
    }
  ];
  
  // Filter tips based on active category
  const filteredTips = activeCategory === "all" 
    ? tips 
    : tips.filter(tip => tip.categoryId === activeCategory);
  
  return (
    <main className="pb-72 mx-auto w-full max-w-[400px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-6 pb-12 w-full">
          <header className="w-full text-xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex flex-col justify-center w-full">
                  <h1 className="w-full">
                    Water Conservation Tips
                  </h1>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-4 w-full">
            <div className="px-4 w-full">
              {/* Feature tip - styled to match Image 1 */}
              <div className="relative bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <img 
                      src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/343d1e62d53da6f82818e98ac5b7c83c3cd33ae4?placeholderIfAbsent=true"
                      alt="Featured tip"
                      className="w-9 h-9 object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="bg-blue-50 rounded-full px-3 py-1 inline-block">
                      <span className="text-xs text-blue-700 font-medium">Featured Tip</span>
                    </div>
                    <h2 className="text-base font-semibold mt-1 text-gray-900">Dual-Flush Toilets</h2>
                    <p className="text-sm text-gray-700 mt-1 line-clamp-2">Installing dual-flush toilets can save up to 68% of water used compared to conventional toilets.</p>
                  </div>
                </div>
                <div className="absolute top-3 right-3">
                  <div className="bg-green-50 rounded-full px-3 py-1 inline-block">
                    <span className="text-xs text-green-700 font-medium">Save 12,000L yearly</span>
                  </div>
                </div>
              </div>
              
              {/* Category selection */}
              <div className="mt-4 overflow-x-auto">
                <div className="flex gap-2 pb-1.5">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`flex flex-col items-center p-2 min-w-[70px] rounded-lg ${
                        activeCategory === category.id 
                          ? 'bg-blue-50 border border-blue-200' 
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <img src={category.icon} alt={category.name} className="w-7 h-7 object-contain" />
                      <span className={`text-xs mt-1.5 ${
                        activeCategory === category.id ? 'text-blue-700 font-medium' : 'text-gray-600'
                      }`}>
                        {category.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Search bar */}
              <div className="mt-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search for tips..."
                    className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-gray-400 absolute left-3 top-2.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
              
              {/* Stats banner */}
              <div className="mt-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-3 border border-blue-200">
                <div className="flex justify-between items-center">
                  <div className="text-center flex-1">
                    <h4 className="text-xs font-medium text-gray-600">Total Tips</h4>
                    <p className="text-lg font-semibold text-blue-600">{tips.length}</p>
                  </div>
                  <div className="h-8 w-px bg-blue-200"></div>
                  <div className="text-center flex-1">
                    <h4 className="text-xs font-medium text-gray-600">Potential Savings</h4>
                    <p className="text-lg font-semibold text-blue-600">250L/day</p>
                  </div>
                </div>
              </div>

              {/* Tips list */}
              <div className="mt-4">
                <h2 className="text-base font-semibold text-gray-900">
                  {activeCategory === "all" ? "All Tips" : categories.find(c => c.id === activeCategory)?.name || "Tips"}
                </h2>
                
                <div className="mt-3 space-y-3">
                  {filteredTips.map(tip => (
                    <div key={tip.id} className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                      <div className="flex items-start gap-3">
                        {tip.image && (
                          <div className={`w-12 h-12 ${tip.categoryId === 'bathroom' ? 'bg-amber-500' : 'bg-blue-600'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <img src={tip.image} alt={tip.title} className="w-7 h-7 object-contain" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <h3 className="text-base font-semibold text-gray-900">{tip.title}</h3>
                          </div>
                          <div className="mt-1">
                            <div className="bg-green-50 rounded-full px-3 py-0.5 inline-block">
                              <span className="text-xs text-green-700 font-medium">{tip.savings}</span>
                            </div>
                          </div>
                          <p className="mt-1.5 text-sm text-gray-700 line-clamp-3">{tip.description}</p>
                          <div className="flex justify-end mt-2">
                            <button className="text-xs text-blue-600 font-medium hover:text-blue-800 transition-colors duration-150 flex items-center">
                              Read more
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Quick facts */}
              <div className="mt-6 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                <h2 className="text-base font-semibold text-gray-900">Water Conservation Facts</h2>
                <ul className="mt-2 space-y-3">
                  <li className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center mt-0.5">
                      <span className="text-xs font-bold">1</span>
                    </div>
                    <p className="text-xs text-gray-700">The average person uses 80-100 gallons (300-380 liters) of water per day.</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center mt-0.5">
                      <span className="text-xs font-bold">2</span>
                    </div>
                    <p className="text-xs text-gray-700">A full bathtub requires about 70 gallons (265 liters) of water, while a 5-minute shower uses 10-25 gallons (38-95 liters).</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center mt-0.5">
                      <span className="text-xs font-bold">3</span>
                    </div>
                    <p className="text-xs text-gray-700">A running toilet can waste up to 200 gallons (757 liters) of water daily.</p>
                  </li>
                </ul>
              </div>
              
              {/* Action button */}
              <div className="mt-4">
                <a href="/conservation" className="w-full block bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-4 py-3 text-sm text-center transition duration-150 ease-in-out">
                  Return to Conservation Hub
                </a>
              </div>
            </div>
          </section>
        </section>
      </div>

      <TabBar />
    </main>
  );
};

export default WaterConservationTips;
